import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { closeModal, openModal, showToastMessage } from 'enable-ui';
import { ApiStoreKey } from 'enums';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { RoleKey, TrainerRoleKey } from 'modules/auth/core/enum';
import { MODAL_KEY } from 'modules/auth/core/modal-key';
import { useFetchApiClassDetail } from 'modules/learners/core/hook';
import type { Learner } from 'modules/learners/core/type';
import type { Trainer } from 'modules/trainers/core/type';

import type { Classes, GroupInClass } from '../type';

import { trimValue } from 'helpers';
import { useBoolean, useNumber, useString } from 'hooks';
import { apiCall, useFetchStore, valueByStateKeySelector } from 'hooks/service';
import { cloneDeep, debounce, filter, isEmpty, isUndefined, orderBy, uniqBy } from 'lodash';

import { PATH } from 'constants/path';

export const useClassesMembersController = () => {
  const navigationTo = useNavigate();

  const params = useParams();

  const { id } = params;

  const userProfile = useAuthStore(userProfileSelector);

  const [selectedMembers, setSelectedMembers] = useState<Record<string, string>>({});

  const [trainerData, setTrainerData] = useState<Trainer[]>([]);

  const [learnerData, setLearnerData] = useState<Learner[]>([]);

  const [pageLearner, setPageLearner] = useState<number>(1);

  const [pageTrainer, setPageTrainer] = useState<number>(1);

  const hasMoreTrainer = useBoolean(false);

  const hasMoreLearner = useBoolean(false);

  const totalPageTrainer = useNumber(0);

  const totalPageLearner = useNumber(0);

  const memberSelected = useString('');

  const searchTrainer = useString('');

  const searchLearner = useString('');

  const searchValue = useString('');

  const institutionId = userProfile?.institution?.id || '';

  const [classDetail, setClassDetail] = useState<Classes>();

  const { fetchApiClassDetail } = useFetchApiClassDetail(id ?? '');

  const classes: Classes = useFetchStore(valueByStateKeySelector(ApiStoreKey.class));

  const [groupInClass, setGroupInClass] = useState<GroupInClass[]>([]);

  const institutionIdRef = useRef(institutionId);

  const isLoading = useBoolean(false);

  useEffect(() => {
    if (!id) {
      return;
    }

    setClassDetail(classes);

    institutionIdRef.current = classes?.institution?.id ?? '';
  }, [classes]);

  useEffect(() => {
    if (!classDetail?.institution?.id) {
      return;
    }

    getListTrainer();
    getListLearner();
  }, [classDetail?.institution?.id]);

  useEffect(() => {
    getListGroupInClass();
  }, []);

  useEffect(() => {
    if (pageLearner > 1) {
      getListLearner();
    }
  }, [pageLearner]);

  useEffect(() => {
    if (pageTrainer > 1) {
      getListTrainer();
    }
  }, [pageTrainer]);

  const getListTrainer = async (search: string = '') => {
    const userInClass = classDetail?.trainers?.map((el) => el.id) || [];

    const paramsApi = new URLSearchParams({
      page: pageTrainer.toString(),
      limit: '10',
      roleKey: RoleKey.TRAINER_INSTITUTION,
      institutionId: institutionIdRef.current,
      search,
    });

    userInClass.forEach((userId) => paramsApi.append('userInClass', userId));

    const result: { items: Trainer[]; total: number } = await apiCall({
      url: `/users?${paramsApi.toString()}`,
      method: 'GET',
    });

    hasMoreTrainer.setValue(result?.items?.length === 10);

    setTimeout(() => {
      isLoading.setValue(false);
    }, 1000);

    if (result?.items) {
      if (pageTrainer === 1) {
        setTrainerData(result.items);
        totalPageTrainer.setValue(result.total);
      } else if (pageTrainer > 1) {
        setTrainerData((prev) => [...prev, ...result.items]);
      }
    }
  };

  const getListLearner = async (search: string = '') => {
    const userInClass = classDetail?.learners?.map((el) => el.id) || [];

    const paramsApi = new URLSearchParams({
      page: pageLearner.toString(),
      limit: '10',
      roleKey: RoleKey.LEARNER,
      institutionId: institutionIdRef.current,
      search,
    });

    userInClass.forEach((userId) => paramsApi.append('userInClass', userId));

    const result: { items: Learner[]; total: number } = await apiCall({
      url: `/users?${paramsApi.toString()}`,
      method: 'GET',
    });

    hasMoreLearner.setValue(result?.items?.length === 10);

    setTimeout(() => {
      isLoading.setValue(false);
    }, 1000);

    if (result?.items) {
      if (pageLearner === 1) {
        setLearnerData(result.items);
        totalPageLearner.setValue(result.total);
      } else if (pageLearner > 1) {
        setLearnerData((prev) => [...prev, ...result.items]);
      }
    }
  };

  const getListGroupInClass = async () => {
    const result: { items: GroupInClass[] } = await apiCall({
      url: `/user-class-groups`,
      method: 'GET',
      data: {
        classId: id,
      },
    });

    if (result?.items) {
      setGroupInClass(orderBy(result.items, 'createdAt', 'asc'));
    }
  };

  const onClickAction = async (
    key: 'view' | 'delete',
    itemId: string,
    userClassId?: string,
    type?: 'trainer' | 'learner',
  ) => {
    switch (key) {
      case 'view':
        if (type === 'trainer') {
          navigationTo(`${PATH.TRAINERS}/${PATH.TRAINER_FULL_TIME_DETAIL.replace(':id', itemId)}`);
        } else {
          navigationTo(`${PATH.LEARNERS}/${PATH.LEARNER_EDIT.replace(':id', itemId)}`);
        }

        break;

      case 'delete':
        memberSelected.setValue(userClassId ?? '');
        openModal(MODAL_KEY.REMOVE_MEMBER);
        break;
    }
  };

  const goToLearnScorePage = (learnerId: string) => {
    navigationTo(PATH.LEARNER_SCORE.replace(':id', learnerId), { state: { classId: id } });
  };

  const listTrainer = useMemo(() => {
    const trainersInClass = classDetail?.trainers?.map((el) => el.id);

    return trainerData?.filter((el) => !trainersInClass?.includes(el.id)) ?? [];
  }, [trainerData, classDetail?.trainers]);

  const listLearner = useMemo(() => {
    const learnersInClass = classDetail?.learners?.map((el) => el.id);

    return learnerData?.filter((el) => !learnersInClass?.includes(el.id)) ?? [];
  }, [learnerData, classDetail?.learners]);

  const onChangeSelectedMembers = (member: string) => () => {
    const newSelected = cloneDeep(selectedMembers);

    if (!isUndefined(newSelected[member])) {
      delete newSelected[member];
    } else {
      newSelected[member] = member;
    }

    setSelectedMembers(newSelected);
  };

  const handleUpdateMembers = async (type: 'trainer' | 'learner') => {
    if (isEmpty(selectedMembers)) {
      return;
    }

    const result: {
      message: string;
      isSuccess: boolean;
    }[] = await apiCall({
      url: `/classes/${id}/add-members `,
      method: 'PATCH',
      data: {
        memberIds: Object.keys(selectedMembers),
        type,
      },
      isLoading: true,
      showError: true,
    });

    if (!isEmpty(result)) {
      setSelectedMembers({});
      fetchApiClassDetail();

      const newMessages = uniqBy(result, 'message');

      newMessages.forEach((el) => {
        showToastMessage(el.isSuccess ? 'success' : 'error', el.message);
      });
    }
  };

  const handleRemoveMember = async () => {
    if (memberSelected.value) {
      await apiCall({
        url: `/classes/${id}/delete-member/${memberSelected.value}`,
        method: 'DELETE',
        isLoading: true,
        showError: true,
      });
      closeModal(MODAL_KEY.REMOVE_MEMBER);

      showToastMessage('success', 'Removed member successfully!');

      fetchApiClassDetail();
    }
  };

  const disabledRemoveTrainer = useMemo(() => {
    const trainersAdmin =
      classDetail?.trainers?.filter(
        (el) =>
          String(el.trainerRole?.key) === TrainerRoleKey.COACH_MANAGER ||
          String(el.trainerRole?.key) === TrainerRoleKey.TRAIN_THE_TRAINER,
      ) ?? [];

    return trainersAdmin.length <= 1;
  }, [classDetail?.trainers]);

  const onChangeSearchTrainer = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageTrainer(1);
    searchTrainer.setValue(event.target.value);
    debounceSearchTrainer(event.target.value);
  };

  const debounceSearchTrainer = useCallback(
    debounce((newSearch: string) => {
      getListTrainer(trimValue(newSearch));
    }, 500),
    [],
  );

  const onChangeSearchLearner = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageLearner(1);
    searchLearner.setValue(event.target.value);
    debounceSearchLearner(event.target.value);
  };

  const debounceSearchLearner = useCallback(
    debounce((newSearch: string) => {
      getListLearner(trimValue(newSearch));
    }, 500),
    [],
  );

  const onChangeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchValue.setValue(event.target.value);
    debounceSearchValue(event.target.value);
  };

  const debounceSearchValue = useCallback(
    debounce((newSearch: string) => {
      const searchTerm = trimValue(newSearch.toLowerCase());

      const searchPhone = searchTerm.replace(/\s/g, '');

      const newTrainers = filter(
        classes?.trainers ?? [],
        (el) =>
          (el.lastName + ' ' + el.firstName)?.toLowerCase().includes(searchTerm) ||
          el.name.toLowerCase().includes(searchTerm) ||
          el.nickname?.toLowerCase().includes(searchTerm) ||
          el.phone?.slice(-searchPhone.length)?.includes(searchPhone),
      );

      const newLearners = filter(
        classes?.learners ?? [],
        (el) =>
          (el.lastName + ' ' + el.firstName)?.toLowerCase().includes(searchTerm) ||
          el.name.toLowerCase().includes(searchTerm) ||
          el.nickname?.toLowerCase().includes(searchTerm) ||
          el.phone?.slice(-searchPhone.length)?.includes(searchPhone),
      );

      setClassDetail({
        ...classes,
        learners: newLearners ?? [],
        trainers: newTrainers ?? [],
        totalLearner: newLearners.length,
        totalTrainer: newTrainers.length,
      } as Classes);
    }, 500),
    [classes],
  );

  const handleAddGroup = async () => {
    const result: { class: Classes } = await apiCall({
      url: `/user-class-groups`,
      method: 'POST',
      data: { classId: id },
      isLoading: true,
      showError: true,
    });

    if (result?.class?.id) {
      showToastMessage('success', 'Added group successfully!');
      getListGroupInClass();
    }
  };

  const handleAddLearnerToGroup = async (groupId: string, userId: string) => {
    await apiCall({
      url: `/classes/${id}/add-member-to-group`,
      method: 'PATCH',
      data: {
        groupId: groupId ? groupId : null,
        userId,
      },
      isLoading: true,
      showError: true,
    });
    fetchApiClassDetail();
  };

  const groupLearners = useMemo(() => {
    return [
      {
        group: 'No group',
        id: '',
        index: 0,
        learners: classDetail?.learners?.filter((item) => !item.userClassGroupId) ?? [],
      },
      ...orderBy(groupInClass, 'createdAt', 'asc').map((el, idx) => ({
        group: `Group ${idx + 1}`,
        id: el.id,
        index: el.index,
        learners: classDetail?.learners?.filter((item) => item.userClassGroupId === el.id) ?? [],
      })),
    ];
  }, [JSON.stringify(groupInClass), classDetail?.learners]);

  const handleDeleteGroup = async (groupId: string) => {
    const result: { id: string } = await apiCall({
      url: `/user-class-groups/${groupId}`,
      method: 'DELETE',
      isLoading: true,
    });

    if (result?.id) {
      showToastMessage(
        'success',
        'Removed group successfully! All clients were moved to No group.',
      );
      getListGroupInClass();
      fetchApiClassDetail();
    } else {
      showToastMessage('error', 'Failed to remove.');
    }
  };

  const handleLoadMoreLearner = () => {
    if (!hasMoreLearner.value || isLoading.value) {
      return;
    }

    isLoading.setValue(true);

    setTimeout(() => {
      setPageLearner((prev) => prev + 1);
    }, 1200);
  };

  const handleLoadMoreTrainer = () => {
    if (!hasMoreTrainer.value || isLoading.value) {
      return;
    }

    isLoading.setValue(true);

    setTimeout(() => {
      setPageTrainer((prev) => prev + 1);
    }, 1200);
  };

  return {
    onClickAction,
    goToLearnScorePage,
    classes: classDetail,
    listTrainer,
    selectedMembers,
    onChangeSelectedMembers,
    handleUpdateMembers,
    handleRemoveMember,
    disabledRemoveTrainer,
    searchTrainer,
    onChangeSearchTrainer,
    listLearner,
    searchLearner,
    onChangeSearchLearner,
    searchValue,
    onChangeSearchValue,
    handleAddGroup,
    groupInClass,
    handleAddLearnerToGroup,
    groupLearners: orderBy(groupLearners, 'index'),
    handleDeleteGroup,
    handleLoadMoreLearner,
    hasMoreLearner,
    hasMoreTrainer,
    handleLoadMoreTrainer,
  };
};
